/**
 * resourceUsage.js — generic "which widgets reference this resource" utilities.
 *
 * Resource-agnostic: every function is parameterized by the schema property
 * name to look at (e.g. 'dataset') rather than hardcoding a resource type, so
 * the same walker works for any future reusable project resource (themes,
 * fonts, icons, …) that widgets reference by a single scalar property.
 */

import { getObjectSchema } from '../Hasp/properties/v2/wireFormat.js'
import { getEffectiveObjectHandlers } from '../logic/pageLogic.js'
import { flattenLogicEntries } from '../actions/logicFlowWire.js'

// Walk every non-template-page object across all pages of a layer, calling
// cb(node, page) for each. Mirrors Exporter.js's _walkPageObjects.
function walkPages(layer, cb) {
  if (!layer?.getChildren) return
  for (const page of layer.getChildren()) {
    if (page.isType?.('template_page')) continue
    const walk = (node) => {
      cb(node, page)
      node.getChildren?.().forEach(walk)
    }
    page.getChildren?.().forEach(walk)
  }
}

function describe(node, page) {
  return {
    node,
    page,
    obj: node.obj,
    haspid: node.haspid,
    comment: node.comment || '',
    objectUuid: node.id?.(),
    pageUuid: page.id?.(),
  }
}

/** True when objType's v2 schema declares a top-level `propName` property. */
export function hasSchemaProp(objType, propName) {
  const schema = getObjectSchema(objType)
  return !!schema?.properties?.[propName]
}

/** Widgets whose `[propName]` currently equals `value` exactly. */
export function findWidgetsReferencing(layer, propName, value) {
  if (!value) return []
  const results = []
  walkPages(layer, (node, page) => {
    if (node[propName] === value) results.push(describe(node, page))
  })
  return results
}

/**
 * Widgets with a typed action whose operand references `resourceId`.
 *
 * The other finders here match a single scalar property on the node, which is
 * how every resource referenced by a *property* is found. A typed action's
 * operand isn't a property: it lives per event in `node.actions`
 * (an ordered handler list — see ConfigManager), because one widget can play a
 * different resource on `down` than on `up`. Kept
 * operand-kind agnostic on purpose: any future typed action that references a
 * project resource by uuid is found by this same walker.
 */
export function findWidgetsUsingActionResource(layer, resourceId) {
  if (!resourceId) return []
  const results = []
  walkPages(layer, (node, page) => {
    if (typeof node.obj !== 'string') return
    // Stored handlers plus the event/action nodes wired to this object.
    const actions = getEffectiveObjectHandlers(node)
    if (actions.some(handler => flattenLogicEntries(handler.actions).some(entry => entry?.resourceId === resourceId))) {
      results.push(describe(node, page))
    }
  })
  return results
}

/** Widgets whose type declares `propName` but the value is unset/falsy. */
export function findWidgetsMissingProp(layer, propName) {
  const results = []
  walkPages(layer, (node, page) => {
    if (hasSchemaProp(node.obj, propName) && !node[propName]) {
      results.push(describe(node, page))
    }
  })
  return results
}

/**
 * Widgets whose `[propName]` is set but `resolver(value)` can't find the
 * referenced resource anymore (e.g. it was deleted from its library).
 */
export function findDanglingReferences(layer, propName, resolver) {
  const results = []
  walkPages(layer, (node, page) => {
    const value = node[propName]
    if (value && !resolver(value)) {
      results.push({ ...describe(node, page), missingValue: value })
    }
  })
  return results
}

/**
 * True if any widget currently references `uuid` via `[propName]`. Shared by
 * resource adapters (see src/stores/resourceAdapters/) whose resource type
 * has no pre-existing, type-specific usage helper to preserve — e.g. fonts
 * and images both call this directly rather than each defining their own
 * copy of the same "no layer → false, else check findWidgetsReferencing"
 * logic. Datasets predate this generic adapter layer and keep their own
 * findWidgetsUsingDataset() (src/utils/datasetValidation.js) instead, since
 * that helper's behavior needed to stay byte-for-byte unchanged.
 */
export function isResourceUsed(layer, propName, uuid) {
  if (!layer) return false
  return findWidgetsReferencing(layer, propName, uuid).length > 0
}
